import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  clearAccountSession,
  type DeskChrome,
  type CareerSaveBlob,
  type FlightPhaseId,
  type SyncSnapshot,
} from "@northstar/dispatch-core";
import {
  applyChromeFromDeskPayload,
  applyChromeFromRevision,
  applyCompanionChrome,
  companionChromeFromNanode,
  loadCompanionChrome,
  resetCompanionChrome,
  saveCompanionChrome,
} from "./chrome";
import { COMPANION_SNAPSHOT_ACCOUNT_KEY, NANODE_SYNC_HINT_URL } from "./companion";
import {
  loadCompanionCrewId,
  mergeLiveIntoSnapshot,
  projectCrewSnapshot,
  companyMembershipKey,
  focusCrewAfterCompanyPull,
  saveCompanionCrewId,
  type CompanionCrewSnapshot,
} from "./crewSnapshot";
import { companionRuntimeApiBase, isHostedCompanionApiBase } from "./apiBase";
import { decideLoginBoard } from "./loginBoard";
import { companionTicketWriteBlock } from "./contentXmlPolicy";
import {
  companionChannelActions,
  commitScheduleRevisionAck,
  fallbackFullPullMs,
  LIVE_POLL_MS,
  loadCompanionRefresh,
  refreshPollingActive,
  saveCompanionRefresh,
  SCHEDULE_PULL_RETRY_MS,
  schedulePullDue,
  type CompanionRefreshSettings,
} from "./refreshSettings";
import {
  companionCanProbe,
  companionCanPull,
  companionHosted,
  companionStorage,
  clearCachedSnapshot,
  initialCompanionSettings,
  loadAccountSession,
  loadCachedSnapshot,
  loginNanodeAccount,
  logoutNanodeAccount,
  probeCompanionSync,
  pullCompanionCareer,
  pullCompanionLive,
  pullCompanionRevision,
  pullCompanionSnapshot,
  saveAccountSession,
  saveCachedSnapshot,
  saveSyncSettings,
  writeCompanionAcars,
  writeCompanionTicket,
  type NanodeAccountSession,
  type SyncClientSettings,
  type SyncLinkStatus,
} from "./syncSession";

const HEALTH_MS = 30_000;

export function useCompanion() {
  const storage = companionStorage();
  const hosted = companionHosted();
  const [settings, setSettingsState] = useState<SyncClientSettings>(() => initialCompanionSettings(storage));
  const [account, setAccount] = useState<NanodeAccountSession | null>(() => loadAccountSession(storage));
  const [snapshot, setSnapshot] = useState<SyncSnapshot | null>(() =>
    (() => {
      const session = loadAccountSession(storage);
      return session ? loadCachedSnapshot(storage, session.accountId) : null;
    })(),
  );
  const [crewId, setCrewIdState] = useState(() => loadCompanionCrewId(storage));
  const [chrome, setChromeState] = useState<DeskChrome>(() => {
    const session = loadAccountSession(storage);
    const cached = session ? loadCachedSnapshot(storage, session.accountId) : null;
    return cached ? applyChromeFromDeskPayload(cached.payload, storage) : loadCompanionChrome(storage);
  });
  const [refresh, setRefreshState] = useState<CompanionRefreshSettings>(() => loadCompanionRefresh(storage));
  const [status, setStatus] = useState<SyncLinkStatus>(() =>
    companionCanProbe(initialCompanionSettings(storage)) ? "checking" : "unset",
  );
  const [healthError, setHealthError] = useState("");
  const [note, setNote] = useState("");
  const [boardStatus, setBoardStatus] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [visible, setVisible] = useState(
    () => typeof document === "undefined" || document.visibilityState === "visible",
  );

  const configured = companionCanPull(settings, account);
  const canProbe = companionCanProbe(settings);
  const previousPhaseRef = useRef<FlightPhaseId | null>(null);
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;
  const view: CompanionCrewSnapshot = useMemo(
    () => projectCrewSnapshot(snapshot, { crewId, previousPhase: previousPhaseRef.current }),
    [snapshot, crewId],
  );

  useEffect(() => {
    if (view.ops.phase.fromSim) previousPhaseRef.current = view.ops.phase.id;
  }, [view.ops.phase.fromSim, view.ops.phase.id]);

  const applyChrome = useCallback((next: DeskChrome) => {
    if (typeof document !== "undefined") applyCompanionChrome(document.documentElement, next);
  }, []);

  useEffect(() => {
    applyChrome(chrome);
  }, [applyChrome, chrome]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVis = () => setVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const probe = useCallback(async (next = settings, nextAccount = account) => {
    const result = await probeCompanionSync(next.baseUrl, "checking");
    const pullReady = companionCanPull(next, nextAccount);
    setStatus(result.status);
    setHealthError(
      result.status === "ok" && !pullReady
        ? hosted
          ? "Sign in to pull a crew snapshot — the company token stays on the proxy."
          : "Sign in, then enter the company API token on the pilot logon to pull."
        : result.error,
    );
  }, [account, hosted, settings]);

  useEffect(() => {
    void probe();
  }, [probe]);

  useEffect(() => {
    if (!canProbe) return;
    const id = window.setInterval(() => void probe(), HEALTH_MS);
    return () => window.clearInterval(id);
  }, [canProbe, probe]);

  function setSettings(patch: Partial<SyncClientSettings>) {
    const next = saveSyncSettings({ ...settings, ...patch }, storage);
    setSettingsState(next);
  }

  function setCrewId(next: string) {
    setCrewIdState(saveCompanionCrewId(next, storage));
  }

  function setRefresh(patch: Partial<CompanionRefreshSettings>) {
    setRefreshState(saveCompanionRefresh({ ...refresh, ...patch }, storage));
  }

  function applySnapshot(
    next: SyncSnapshot,
    opts?: {
      accountId?: string;
      accountEmail?: string;
      careerName?: string;
      careerEmail?: string;
      career?: CareerSaveBlob | null;
    },
  ) {
    saveCachedSnapshot(next, storage, opts?.accountId ?? account?.accountId);
    snapshotRef.current = next;
    setSnapshot(next);
    const chromeNext = saveCompanionChrome(
      companionChromeFromNanode({ snapshot: next, career: opts?.career ?? null }),
      storage,
    );
    setChromeState(chromeNext);
    applyChrome(chromeNext);
    const roster = (next.payload.crew ?? []).map((row) => ({ id: row.id, displayName: row.displayName }));
    const focused = focusCrewAfterCompanyPull({
      roster,
      previousCrewId: loadCompanionCrewId(storage),
      previousCompanyKey: companyMembershipKey(snapshot?.payload.company),
      nextCompanyKey: companyMembershipKey(next.payload.company),
      accountEmail: opts?.accountEmail,
      profileName: opts?.careerName,
      profileEmail: opts?.careerEmail,
    });
    setCrewId(focused);
  }

  async function pull(override?: {
    settings: SyncClientSettings;
    account: NanodeAccountSession | null;
  }): Promise<boolean> {
    const nextSettings = override?.settings ?? settingsRef.current;
    const nextAccount = override?.account ?? accountRef.current;
    setError("");
    setNote("");
    setBusy(true);
    try {
      const result = await pullCompanionSnapshot(nextSettings, nextAccount);
      let career: Awaited<ReturnType<typeof pullCompanionCareer>> | null = null;
      if (nextAccount) {
        career = await pullCompanionCareer(nextSettings, nextAccount);
      }
      const decision = decideLoginBoard({
        state: result.ok ? { ok: true, snapshot: result.snapshot } : result,
        career,
      });
      if (!decision.snapshot) {
        setError(decision.error);
        return false;
      }
      const careerBlob = career?.ok ? career.blob : null;
      applySnapshot(decision.snapshot, {
        accountId: nextAccount?.accountId,
        accountEmail: nextAccount?.email,
        careerName: careerBlob?.payload.profile.name ?? "",
        careerEmail: careerBlob?.payload.profile.email ?? "",
        career: careerBlob,
      });
      scheduleRevRef.current = decision.scheduleRevision;
      if (decision.scheduleRevision) {
        scheduleRetryAtRef.current = 0;
      }
      setBoardStatus(decision.boardStatus);
      setNote(
        decision.boardStatus
          ? decision.boardStatus
          : "Pulled this account’s joined-company snapshot. Theme matches the desk.",
      );
      try {
        await probe(nextSettings, nextAccount);
      } catch {
        // The board is already applied. A health-probe miss must not look like a missed schedule pull.
      }
      return true;
    } catch {
      setError("Pull failed.");
      return false;
    } finally {
      setBusy(false);
    }
  }

  const pullRef = useRef(pull);
  pullRef.current = pull;
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const accountRef = useRef(account);
  accountRef.current = account;
  const scheduleRevRef = useRef("");
  const scheduleAttemptRef = useRef("");
  const scheduleRetryAtRef = useRef(0);
  const liveRevRef = useRef("");
  const trailRevRef = useRef("");
  const pullingRef = useRef(false);
  const revisionOkRef = useRef(false);

  async function applyLiveSlice(
    nextSettings: typeof settings,
    nextAccount: typeof account,
    liveRevisionHint = "",
  ) {
    const live = await pullCompanionLive(nextSettings, nextAccount);
    if (!live.ok || !snapshotRef.current) return;
    let slice = live.live;
    const trailMoved = Boolean(slice.trailRevision) && slice.trailRevision !== trailRevRef.current;
    if (trailMoved) {
      const full = await pullCompanionLive(nextSettings, nextAccount, { trail: true });
      if (full.ok && full.live.flightTrack) {
        slice = full.live;
        trailRevRef.current = full.live.trailRevision || slice.trailRevision;
      }
    }
    const nextRev = slice.liveRevision || liveRevisionHint;
    if (!trailMoved && nextRev && nextRev === liveRevRef.current) return;
    liveRevRef.current = nextRev;
    const merged = mergeLiveIntoSnapshot(snapshotRef.current, slice);
    snapshotRef.current = merged;
    saveCachedSnapshot(merged, storage, nextAccount?.accountId);
    setSnapshot(merged);
  }

  async function tickChannels() {
    const nextSettings = settingsRef.current;
    const nextAccount = accountRef.current;
    if (!companionCanPull(nextSettings, nextAccount) || pullingRef.current) return;
    pullingRef.current = true;
    try {
      const rev = await pullCompanionRevision(nextSettings, nextAccount);
      revisionOkRef.current = rev.ok;
      // Revision theme can be a stale company.theme ("light") while deskChrome is night.
      // Apply that ping only before any snapshot exists. After a pull, snapshot/career chrome sticks.
      if (rev.ok && !snapshotRef.current) {
        const fromRev = applyChromeFromRevision(rev.revision, storage);
        if (fromRev) {
          setChromeState(fromRev);
          applyChrome(fromRev);
        }
      }
      const remoteScheduleRevision = rev.ok ? rev.revision.scheduleRevision : undefined;
      const actions = companionChannelActions({
        revisionOk: rev.ok,
        remoteScheduleRevision,
        lastScheduleRevision: scheduleRevRef.current,
        hasSnapshot: Boolean(snapshotRef.current),
      });
      const pullSchedule = schedulePullDue({
        revisionOk: rev.ok,
        remoteScheduleRevision,
        appliedScheduleRevision: scheduleRevRef.current,
        attemptedScheduleRevision: scheduleAttemptRef.current,
        nowMs: Date.now(),
        retryNotBeforeMs: scheduleRetryAtRef.current,
      });
      if (pullSchedule && remoteScheduleRevision) {
        scheduleAttemptRef.current = remoteScheduleRevision;
        const previousAck = scheduleRevRef.current;
        const pulled = await pullRef.current();
        const committed = commitScheduleRevisionAck({
          previousAck,
          pullOk: pulled,
          advertisedRevision: remoteScheduleRevision,
          receivedRevision: pulled ? scheduleRevRef.current : "",
        });
        scheduleRevRef.current = committed.ack;
        if (!pulled || committed.mismatch) {
          scheduleRetryAtRef.current = Date.now() + SCHEDULE_PULL_RETRY_MS;
          if (committed.mismatch && committed.notice) setNote(committed.notice);
        } else {
          scheduleRetryAtRef.current = 0;
        }
      }
      if (actions.pullLive) {
        await applyLiveSlice(nextSettings, nextAccount, rev.ok ? rev.revision.liveRevision : "");
      }
    } finally {
      pullingRef.current = false;
    }
  }

  useEffect(() => {
    if (!configured || !refreshPollingActive(refresh.mode, visible)) return;
    void tickChannels();
    const id = window.setInterval(() => void tickChannels(), LIVE_POLL_MS);
    return () => window.clearInterval(id);
  }, [configured, refresh.mode, visible]);

  useEffect(() => {
    if (!configured || !refreshPollingActive(refresh.mode, visible)) return;
    const id = window.setInterval(() => {
      if (pullingRef.current || revisionOkRef.current) return;
      void pullRef.current();
    }, fallbackFullPullMs(refresh));
    return () => window.clearInterval(id);
  }, [configured, refresh, visible]);

  function setChrome(patch: Partial<{ theme: unknown; themePack: unknown; themeAccent: unknown }>) {
    const next = saveCompanionChrome(patch, storage);
    setChromeState(next);
    applyChrome(next);
  }

  async function signIn(input: { email: string; password: string; baseUrl?: string; token?: string }) {
    setError("");
    setNote("");
    setBusy(true);
    try {
      const runtimeBase = companionRuntimeApiBase();
      const baseUrl = input.baseUrl?.trim() || settings.baseUrl || runtimeBase || NANODE_SYNC_HINT_URL;
      const token = input.token !== undefined ? input.token : settings.token;
      const out = await loginNanodeAccount(baseUrl, { email: input.email, password: input.password });
      if (!out.ok) {
        setError(out.error);
        return;
      }
      const session = { email: out.email, accountId: out.accountId, token: out.token, baseUrl };
      const owner = storage?.getItem(COMPANION_SNAPSHOT_ACCOUNT_KEY) ?? "";
      scheduleRevRef.current = "";
      scheduleAttemptRef.current = "";
      scheduleRetryAtRef.current = 0;
      liveRevRef.current = "";
      if (owner !== session.accountId) {
        clearCachedSnapshot(storage);
        setCrewIdState(saveCompanionCrewId("", storage));
        setSnapshot(null);
        snapshotRef.current = null;
        const chromeNext = resetCompanionChrome(storage);
        setChromeState(chromeNext);
        applyChrome(chromeNext);
        setBoardStatus("");
      }
      saveAccountSession(session, storage);
      setAccount(session);
      const nextSettings = hosted
        ? { ...settings, baseUrl: isHostedCompanionApiBase(baseUrl) ? baseUrl : runtimeBase, token }
        : { ...settings, baseUrl, token };
      if (
        nextSettings.baseUrl !== settings.baseUrl ||
        nextSettings.token !== settings.token ||
        !settings.baseUrl.trim()
      ) {
        setSettings({ baseUrl: nextSettings.baseUrl, token: nextSettings.token });
      }
      await probe(nextSettings, session);
      if (!companionCanPull(nextSettings, session)) {
        setError(
          hosted
            ? "Signed in, but this phone cannot pull the company board yet."
            : "Signed in. Enter the company sync token on the pilot logon, then sign in again.",
        );
        return;
      }
      const pulled = await pull({ settings: nextSettings, account: session });
      if (!pulled) {
        setNote("Signed in. The company board did not pull. Retry is automatic.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function writeTicket(input: {
    missionId: string;
    action: "cancel" | "edit" | "reassign_crew";
    cancelReason?: string;
    cancelReasonNote?: string;
    departureIso?: string;
    arrivalIso?: string;
    paxCount?: number;
    cargoLb?: number;
    originIcao?: string;
    destIcao?: string;
    crewHint?: string;
    acceptMissingScenery?: boolean;
  }) {
    setError("");
    setNote("");
    const trip = view.trips.find((row) => row.id === input.missionId) ?? view.nextTrip;
    const blocked = companionTicketWriteBlock(
      view.schedulePolicy,
      { originIcao: input.originIcao, destIcao: input.destIcao },
      trip ?? undefined,
    );
    if (blocked) {
      setError(blocked);
      return { ok: false as const, error: blocked };
    }
    setBusy(true);
    try {
      const result = await writeCompanionTicket(settings, account, input);
      if (!result.ok) {
        setError(result.error);
        return result;
      }
      setNote(`${result.summary}. Pulling the company board so the Sim desk stays source of truth.`);
      await pullRef.current();
      return result;
    } finally {
      setBusy(false);
    }
  }

  async function writeAcars(missionId: string, packet: string) {
    setError("");
    setNote("");
    if (!missionId) return { ok: false as const, error: "No trip on this board." };
    setBusy(true);
    try {
      const result = await writeCompanionAcars(settings, account, { missionId, packet });
      if (!result.ok) {
        setError(result.error);
        return result;
      }
      setNote("Message filed. Pulling the company board.");
      await pullRef.current();
      return { ok: true as const };
    } finally {
      setBusy(false);
    }
  }

  async function signOut() {
    if (account) await logoutNanodeAccount(account.baseUrl, account.token);
    clearAccountSession(storage);
    clearCachedSnapshot(storage);
    setCrewIdState(saveCompanionCrewId("", storage));
    setAccount(null);
    setSnapshot(null);
    setBoardStatus("");
    const chromeNext = resetCompanionChrome(storage);
    setChromeState(chromeNext);
    applyChrome(chromeNext);
    scheduleRevRef.current = "";
    scheduleAttemptRef.current = "";
    scheduleRetryAtRef.current = 0;
    liveRevRef.current = "";
    setNote("Signed out. This phone no longer shows the previous account’s board.");
  }

  return {
    settings,
    setSettings,
    account,
    snapshot,
    view,
    crewId,
    setCrewId,
    chrome,
    setChrome,
    refresh,
    setRefresh,
    status,
    hosted,
    canProbe,
    configured,
    healthError,
    note,
    boardStatus,
    error,
    busy,
    writeTicket,
    writeAcars,
    pull,
    probe,
    signIn,
    signOut,
  };
}

export type CompanionApi = ReturnType<typeof useCompanion>;
